import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    overflowY: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1)
    }
  },
  content: {
    display: "grid",
    gap: theme.spacing(2)
  },
  summary: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: "grid",
    gap: theme.spacing(1),
    padding: theme.spacing(2)
  },
  total: {
    fontSize: 32,
    fontWeight: 800,
    lineHeight: 1.1
  },
  muted: {
    color: theme.palette.text.secondary
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  }
}));

const formatMoney = (amountCents, currency = "usd") =>
  new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
    maximumFractionDigits: 0
  }).format((Number(amountCents) || 0) / 100);

const normalizeDate = value => {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return date.toLocaleDateString("es-CR", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
};

const Billing = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [company, setCompany] = useState(null);
  const [config, setConfig] = useState(null);
  const [limits, setLimits] = useState({
    maxUsers: 3,
    maxConnections: 1
  });

  const fetchBilling = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing");
      setCompany(data.company);
      setConfig(data.config);
      setLimits({
        maxUsers: data.company?.maxUsers || data.config?.baseUsers || 3,
        maxConnections:
          data.company?.maxConnections || data.config?.baseConnections || 1
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
  }, []);

  const preview = useMemo(() => {
    const billingConfig = config || {};
    const maxUsers = Math.max(
      Number(billingConfig.baseUsers || 3),
      Number(limits.maxUsers || billingConfig.baseUsers || 3)
    );
    const maxConnections = Math.max(
      Number(billingConfig.baseConnections || 1),
      Number(limits.maxConnections || billingConfig.baseConnections || 1)
    );
    const extraUsers = Math.max(0, maxUsers - Number(billingConfig.baseUsers || 3));
    const extraConnections = Math.max(
      0,
      maxConnections - Number(billingConfig.baseConnections || 1)
    );
    const total =
      Number(billingConfig.baseAmount || 4900) +
      extraUsers * Number(billingConfig.userAmount || 1600) +
      extraConnections * Number(billingConfig.connectionAmount || 2000);

    return {
      maxUsers,
      maxConnections,
      extraUsers,
      extraConnections,
      total
    };
  }, [config, limits]);

  const handleLimitChange = field => event => {
    setLimits(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCheckout = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        maxUsers: preview.maxUsers,
        maxConnections: preview.maxConnections
      });

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      toast.success("Facturación actualizada");
      fetchBilling();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePortal = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/portal");
      if (data.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Facturación</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" onClick={fetchBilling}>
            Actualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        {loading ? (
          <CircularProgress />
        ) : (
          <div className={classes.content}>
            <div className={classes.summary}>
              <Typography variant="h6">Plan actual</Typography>
              <Typography className={classes.muted}>
                {company?.name || "Cuenta"} · Estado: {company?.status || "sin estado"}
              </Typography>
              <Typography className={classes.total}>
                {formatMoney(preview.total, config?.currency)}
                <Typography component="span" className={classes.muted}>
                  {" "}
                  / mes
                </Typography>
              </Typography>
              <Typography className={classes.muted}>
                Próxima expiración: {normalizeDate(company?.expiresAt)}
              </Typography>
              <Typography className={classes.muted}>
                Base incluida: {config?.baseConnections || 1} conexión y{" "}
                {config?.baseUsers || 3} usuarios.
              </Typography>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Usuarios"
                  type="number"
                  variant="outlined"
                  value={limits.maxUsers}
                  inputProps={{ min: config?.baseUsers || 3 }}
                  onChange={handleLimitChange("maxUsers")}
                  helperText={`${preview.extraUsers} usuarios adicionales · ${formatMoney(
                    Number(config?.userAmount || 1600),
                    config?.currency
                  )} cada uno`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Conexiones"
                  type="number"
                  variant="outlined"
                  value={limits.maxConnections}
                  inputProps={{ min: config?.baseConnections || 1 }}
                  onChange={handleLimitChange("maxConnections")}
                  helperText={`${preview.extraConnections} conexiones adicionales · ${formatMoney(
                    Number(config?.connectionAmount || 2000),
                    config?.currency
                  )} cada una`}
                />
              </Grid>
            </Grid>

            <div className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                disabled={submitting}
                onClick={handleCheckout}
              >
                {company?.stripeSubscriptionId ? "Actualizar plan" : "Contratar o renovar"}
              </Button>
              {company?.stripeCustomerId && (
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={submitting}
                  onClick={handlePortal}
                >
                  Portal de facturación
                </Button>
              )}
            </div>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Billing;
